<template>
    <BasicMenu
        id="select-menu"
        ref="basicMenu"
        class="!block relative"
        :class="basicMenuClasses"
        :disabled="disabled"
        :items="items"
        :is-default-menu="false"
        :interactive="false"
        max-height-class="max-h-[345px]"
        :show="isOpen"
        @click:item="onItemClick"
    >
        <template #activator>
            <BasicFormControl
                v-bind="props"
                :id="id"
                :class="{ 'cursor-pointer': !disabled }"
                :has-error="hasError"
                :custom-border="customBorder"
                :error-message="computedError"
                :basic-error-message-id="basicErrorMessageId"
                :bottom-border-rounded="bottomBorderRounded"
                :top-border-rounded="topBorderRounded"
                :is-focused="isFocused"
                @click:focus="focusInput($event)"
                @click:prepend-inner-icon="
                    emits('click:prependInnerIcon', localModelValue)
                "
                @click:append-inner-icon="
                    emits('click:appendInnerIcon', localModelValue)
                "
            >
                <template
                    v-if="$slots.default"
                    #default
                >
                    <slot></slot>
                </template>
                <template
                    v-if="$slots.prependInner"
                    #prependInner
                >
                    <slot name="prependInner"> </slot>
                </template>

                <template #appendInner>
                    <div class="flex-center">
                        <BasicIcon
                            v-if="localModelValue && isOpen"
                            name="check"
                            size="8"
                            class="fill-font-3"
                        />
                        <BasicIcon
                            v-else
                            class="select-caret fill-font-3"
                            :class="{ open: isOpen }"
                            name="caret_down"
                            size="8"
                        />
                        <slot name="appendInner"> </slot>
                    </div>
                </template>
                <template #input>
                    <div class="flex-1 leading-[22px]">
                        <slot
                            name="selected"
                            v-bind="{ text: valueText, value: localModelValue }"
                        >
                            {{ valueText }}
                        </slot>
                        <input
                            :id="id"
                            ref="input"
                            :disabled="disabled"
                            :aria-describedby="basicErrorMessageId"
                            :required="required"
                            :aria-invalid="hasError"
                            :value="localModelValue"
                            class="w-0 appearance-none focus:outline-none caret-transparent text-transparent opacity-0 cursor-pointer"
                            @focus="onFocus"
                        />
                    </div>
                </template>
            </BasicFormControl>
        </template>
        <template #outter:item="data">
            <slot
                name="outter:item"
                v-bind="{ data, selected: localModelValue === data.name }"
            >
                <BasicMenuListItem
                    v-bind="data"
                    class="!border-none font-normal fade hover:!bg-background-2 rounded-[5px] !p-[10px]"
                    :class="{
                        '!bg-background-2': data.name === highlighted?.name,
                    }"
                >
                    <template #wrapper>
                        <div class="flex-center flex-1">
                            <BasicIcon
                                v-if="data.icon"
                                :name="data.icon"
                                :size="data.iconSize || '20'"
                                :inherit-fill="false"
                                class="mr-[10px]"
                            />
                            <div
                                class="flex-1 !leading-[22px]"
                                :class="textSizeClass"
                            >
                                {{ data.text }}
                            </div>
                            <BasicIcon
                                v-if="localModelValue === data.name"
                                class="fill-font-3"
                                name="check"
                                size="8"
                            />
                        </div>
                    </template>
                </BasicMenuListItem>
            </slot>
        </template>
    </BasicMenu>
</template>

<script setup lang="ts">
import BasicFormControl, {
    type BasicFormControlSlots,
} from '@atoms/BasicFormControl/BasicFormControl.vue';
import BasicIcon from '@atoms/BasicIcon/BasicIcon.vue';
import BasicMenu from '@molecules/BasicMenu/BasicMenu.vue';
import BasicMenuListItem, {
    type BasicMenuListItemProps,
} from '@molecules/BasicMenu/BasicMenuListItem.vue';

import { type Size } from '@atoms/BasicFormControl/config';
import { type Size as BasicIconSize } from '@atoms/BasicIcon/sizes';

import { type InputEmits, type InputProps } from '@composables/useInput';
import { useSelectNavigation } from '@composables/useSelectNavigation';
import { type ValidateOnEvent } from '@composables/useValidation';

import { sleep } from '@utils/time';

export type Item = BasicMenuListItemProps;

export interface BasicSelectProps extends InputProps<string> {
    size?: Size;
    placeholder?: string;
    prependInnerIcon?: string;
    appendInnerIcon?: string;
    appendInnerIconSize?: BasicIconSize;
    prependInnerIconSize?: BasicIconSize;
    validateOn?: ValidateOnEvent[];
    items: Item[];
}

const props = withDefaults(defineProps<BasicSelectProps>(), {
    id: '',
    label: '',
    modelValue: '',
    disabled: false,
    required: false,
    errorMessage: '',
    rules: () => [],
    size: 'm',
    placeholder: '',
    prependInnerIcon: '',
    appendInnerIcon: '',
    prependInnerIconSize: undefined,
    appendInnerIconSize: undefined,
    validateOn: () => ['blur'],
    name: '',
});

export type BasicSelectEmits = InputEmits<BasicSelectProps['modelValue']> & {
    (e: 'click:appendInnerIcon', value?: BasicSelectProps['modelValue']): void;
    (e: 'click:prependInnerIcon', value?: BasicSelectProps['modelValue']): void;
    (e: 'blur'): void;
};

export interface BasicSelectSlots extends Omit<BasicFormControlSlots, 'input'> {
    selected(data: {
        text: BasicMenuListItemProps['text'] | number;
        value: BasicSelectProps['modelValue'];
    }): unknown;
    'outter:item'?(data: {
        selected: boolean;
        data: BasicMenuListItemProps;
    }): unknown;
}

const emits = defineEmits<BasicSelectEmits>();
const localModelValue = ref(props.modelValue || '');
const slots = defineSlots<BasicSelectSlots>();
const basicMenu = ref(null);
const input = ref<HTMLInputElement | null>(null);
const autoPlaced = ref<'bottom' | 'top'>('bottom');

const { id } = useInput(
    'basic-select',
    toRef(() => props.id)
);

const focusInput = (value: boolean) => value && input.value?.focus();

const customBorder = computed(() => {
    if (isFocused.value && !hasError.value) {
        return '!border-primary';
    }
    return undefined;
});

const { hasError, computedError, runValidationHandler } = useValidation(
    toRef(() => props.errorMessage),
    toRef(() => props.rules),
    toRef(() => props.modelValue),
    toRef(() => props.validateOn)
);
const { id: basicErrorMessageId } = useBasicErrorMessage(id, hasError);

const onItemClick = (name: Item['name']) => {
    if (props.disabled || !isOpen.value) return;
    runValidationHandler(name, 'input');
    setIsOpen(false);
    setValue(name);
};

const setValue = (value: Item['name']) => {
    emits('update:modelValue', value);
    localModelValue.value = value;
};

const {
    highlighted,
    isFocused,
    isOpen,
    onValueChange,
    setIsFocused,
    setIsOpen,
    selectNavigationListener,
} = useSelectNavigation({
    modelValue: toRef(() => props.modelValue),
    items: toRef(() => props.items),
});

onValueChange((value) => setValue(value));

onClickOutside(basicMenu, () => {
    setIsOpen(false);
    setIsFocused(false);
});

const bottomBorderRounded = computed(
    () => !isOpen.value || (autoPlaced.value === 'top' && isOpen.value)
);

const topBorderRounded = computed(
    () => !isOpen.value || (autoPlaced.value === 'bottom' && isOpen.value)
);

const setPopperPlacementClass = async (delay?: number) => {
    await sleep(delay || 1);
    const menu = document.getElementById('select-menu');
    const popper = menu?.querySelector('.popper');
    const placement = popper?.getAttribute('data-popper-placement');
    if (popper && placement) {
        if (hasError.value) {
            popper.classList.add('has-error');
        } else {
            popper.classList.remove('has-error');
        }
        if (placement === 'top') {
            autoPlaced.value = 'top';
            popper.classList.remove('bottom');
            popper.classList.add('top');
            if (props.label || slots.default) {
                popper.classList.add('top-mb');
            }
            return;
        }
        autoPlaced.value = 'bottom';
        popper.classList.add('bottom');
        popper.classList.remove('top');
        popper.classList.remove('top-mb');
    }
};

onMounted(() => {
    window.addEventListener('keydown', selectNavigationListener);
    window.addEventListener('resize', () => setPopperPlacementClass());
    window.addEventListener('wheel', () => setPopperPlacementClass(), {
        passive: false,
    });
});
onUnmounted(() => {
    window.removeEventListener('keydown', selectNavigationListener);
    window.removeEventListener('resize', () => setPopperPlacementClass());
    window.removeEventListener('wheel', () => setPopperPlacementClass());
});

watch(
    () => isOpen.value,
    async (newValue) => {
        await setPopperPlacementClass(newValue ? 10 : 150);
    }
);

watch(
    () => isFocused.value,
    (newValue) => {
        if (!newValue) {
            emits('blur');
        }
    }
);

watch(
    () => props.modelValue,
    (newValue) => {
        if (localModelValue.value !== newValue) {
            setValue(newValue);
        }
    }
);

const valueText = computed(() => {
    return (
        props?.items?.find((item) => item.name === localModelValue.value)
            ?.text || localModelValue.value
    );
});

const basicMenuClasses = computed(() => {
    return [props.size, { 'has-error': hasError.value }];
});

const textSizeClass = computed(() => {
    return props.size === 's' ? 'text-xs' : 'text-sm';
});

const onFocus = () => {
    setIsFocused(true);
    setIsOpen(true);
};
</script>

<style scoped>
:deep(.popper) {
    @apply w-full !bg-background-1 !border !border-primary !border-solid !text-font-1;
}

:deep(.popper.bottom) {
    @apply !rounded-t-none !border-t-input !mt-[-1px];
}

:deep(.popper.top) {
    @apply !border-b-input !rounded-b-none !mb-[-1px];
}

:deep(.popper.top-mb) {
    @apply !mb-[-22px];
}

:deep(.popper.bottom.has-error) {
    @apply !mt-[-22px];
}

.has-error :deep(.popper) {
    @apply !border-error;
}

:deep(.basic-menu-list-ul) {
    @apply py-[3px];
}

:deep(.basic-menu-list-li) {
    @apply px-[5px];
}

.select-caret {
    transform: rotate(0deg);
    transition: transform 0.2s linear;
}

.select-caret.open {
    transform: rotate(180deg);
    transition: transform 0.2s linear;
}
</style>
